import { convertTokens } from '@date-fns/upgrade/v2';
import { format as formatDate } from 'date-fns';

export interface PartialDate {
  day?: string;
  month?: string;
  year?: string;
}

export interface PartialPhone {
  area?: string;
  exchange?: string;
  subscriber?: string;
}

export type DateOrPartial = Date | PartialDate;

const valuesFormat = {
  DD: 'day',
  MM: 'month',
  YYYY: 'year',
  YY: 'year'
};

export const dateFormat: Required<PartialDate> = {
  year: 'YYYY',
  month: 'MM',
  day: 'DD'
};

export const pickerFormat: Required<PartialDate> = {
  year: 'Y',
  month: 'm',
  day: 'd'
};

const getFullYear = (year: number) => (year > 100 ? year : year + 2000);

const getSeparator = (format: string) => format.replace(/\w+/, '')[0];

const partialCompleted = (partial: PartialDate, formatSections: string[]) =>
  formatSections.reduce((result, formatSection) => {
    const sectionValue = partial[valuesFormat[formatSection]];

    return result && sectionValue && sectionValue.length === formatSection.length;
  }, true);

const emptyPartial = (partial?: PartialDate) => {
  if (!partial) {
    return true;
  }

  const { day, month, year } = partial;
  return !day && !month && !year;
};

const emptyPartialPhone = (partial?: PartialPhone) => {
  if (!partial) {
    return true;
  }

  const { area, exchange, subscriber } = partial;
  return !area && !exchange && !subscriber;
};

const getPhonePartial = (formatSection: string, section: number) => {
  if (formatSection === 'xxx') {
    return section === 0 ? 'area' : 'exchange';
  }

  return 'subscriber';
};

export const completedPartial = (partial?: PartialDate) => {
  if (!partial) {
    return false;
  }

  const { day, month, year } = partial;
  return day && month && year;
};

const dateFromPartial = (partial: PartialDate) => {
  const { year, month, day } = partial;
  const donor = new Date();
  const resultYear = year ? getFullYear(+year) : donor.getFullYear();
  const resultMonth = month ? +month - 1 : donor.getMonth();
  const resultDay = day ? +day : 1;
  const result = new Date(resultYear, resultMonth, resultDay);

  return result.getDate() === resultDay &&
    result.getMonth() === resultMonth &&
    result.getFullYear() === resultYear
    ? result
    : partial;
};

export const dateFormatFactory = (format: string) => (value: DateOrPartial) => {
  if (value instanceof Date) {
    return formatDate(value, convertTokens(format));
  }

  if (emptyPartial(value)) {
    return '';
  }

  const separator = getSeparator(format);

  return format
    .split(separator)
    .map((formatSection) => {
      const formatValue = value[valuesFormat[formatSection]];

      return formatValue
        ? `${formatValue}${formatSection.substring(0, formatSection.length - formatValue.length)}`
        : formatSection;
    })
    .join(separator);
};

export const dateParseFactory = (format: string) => (value: string) => {
  const separator = getSeparator(format);
  const inputSections = value.split(separator);
  const formatSections = format.split(separator);
  const partial: PartialDate = inputSections.reduce(
    (result: PartialDate, inputSection, section) => {
      const formatSection = formatSections[section];
      const datePortion = valuesFormat[formatSection];

      if (inputSection !== formatSection) {
        const digitsOnly = inputSection.replace(/\D+/g, '');
        if (formatSection === 'YY' && digitsOnly.length === 4) {
          result[datePortion] = digitsOnly.slice(2);
        } else {
          if (digitsOnly.length > formatSection.length) {
            const remaining = digitsOnly.slice(formatSection.length);
            inputSections[section + 1] = remaining;
          }
          result[datePortion] = digitsOnly.slice(0, formatSection.length);
        }
      }

      return result;
    },
    {}
  );

  return partialCompleted(partial, formatSections) ? dateFromPartial(partial) : partial;
};

let previousPosition = 0;
export const setSelectionRange = (
  format: string,
  inputEl: HTMLInputElement,
  currentPosition: number
) => {
  const { value } = inputEl;
  const separator = getSeparator(format);
  const inputSections = value.split(separator);
  const formatSections = format.split(separator);
  const sectionPosition = formatSections.map((format, index) =>
    formatSections.reduce(
      (result, { length }, innerIndex) =>
        innerIndex < index ? result + length + separator.length : result,
      0
    )
  );

  const completedInput = (inputSection: string) => inputSection.replace(/\D+/, '');
  const completedLength = (inputSection: string) => completedInput(inputSection).length;
  const sectionCompleted = (section: number) =>
    completedLength(inputSections[section]) === formatSections[section].length;

  const inputPosition = inputSections.reduceRight<number | null>(
    (result, inputSection, section) => {
      const sectionStart = sectionPosition[section];
      const sectionEnd = sectionStart + inputSection.length;
      const nextSectionStart = sectionPosition[section + 1];
      const previousSection = sectionPosition[section] - 1;
      const completed = sectionCompleted(section);
      const changedPosition = Math.abs(currentPosition - previousPosition);

      if (!result && currentPosition >= sectionStart && currentPosition <= sectionEnd) {
        if (
          !completed &&
          currentPosition === sectionStart &&
          currentPosition > 0 &&
          currentPosition < previousPosition &&
          changedPosition === 1
        ) {
          return previousSection;
        }
        if (
          completed &&
          currentPosition === sectionEnd &&
          nextSectionStart &&
          currentPosition > previousPosition &&
          changedPosition === 1
        ) {
          return nextSectionStart;
        }

        if (
          !completed &&
          currentPosition === sectionStart &&
          currentPosition > 0 &&
          completedLength(inputSection) > 0
        ) {
          return sectionStart + completedLength(inputSection);
        }

        const selection = value[currentPosition];
        if (!selection || !completedInput(selection)) {
          return sectionStart + completedLength(inputSection);
        }
      }

      return result;
    },
    null
  );
  previousPosition = currentPosition;
  const position = inputPosition || currentPosition;

  inputEl.setSelectionRange(position, position);
};

export const getIntlDateFormat = (
  parts: Intl.DateTimeFormatPart[],
  format: Required<PartialDate>,
  space?: boolean
) =>
  parts
    .slice(0, 5)
    .map(({ type, value }) => format[type] || value.replace(' ', ''))
    .join(space ? ' ' : '');

export const phoneFormatFactory = (format: string) => (value: PartialPhone) => {
  if (emptyPartialPhone(value) || value.area?.startsWith('0')) return '';

  const separator = getSeparator(format);

  return format
    .split(separator)
    .map((formatSection, index) => {
      const formatValue = value[getPhonePartial(formatSection, index)];

      return formatValue
        ? `${formatValue}${formatSection.substring(0, formatSection.length - formatValue.length)}`
        : formatSection;
    })
    .join(separator);
};

export const phoneParseFactory = (format: string) => (value: string) => {
  const separator = getSeparator(format);
  const inputSections = value.split(separator);
  const formatSections = format.split(separator);
  const partial = inputSections.reduce((result: Partial<PartialPhone>, inputSection, section) => {
    const formatSection = formatSections[section];
    const digitsOnly = inputSection.replace(/\D+/g, '');
    if (digitsOnly.length > formatSection.length) {
      const remaining = digitsOnly.slice(formatSection.length);
      inputSections[section + 1] = remaining;
    }

    result[getPhonePartial(formatSection, section)] = digitsOnly.slice(0, formatSection.length);

    return result;
  }, {});

  return partial;
};
